// Billing Types and Constants
// Country / subdivision data now comes from the canonical module at src/lib/countries.ts.
// Legacy ALL_COUNTRIES / US_STATES / SCA_COUNTRIES are re-exported from it for backward
// compatibility; prefer importing the helpers below (or from '../../lib/countries') going forward.

import {
  COUNTRIES,
  SUBDIVISIONS,
  STRIPE_SET,
  getSubdivisions as _getSubdivisions,
  postalRule as _postalRule,
  showTaxIdField as _showTaxIdField,
  gatewayFor as _gatewayFor,
} from '../../lib/countries';
import type { Country, Subdivision } from '../../lib/countries';

export type { Country, Subdivision };

export interface BillingAccount {
  id: number;
  name: string;
  is_default: boolean;
  chargebee_customer_id: string | null;
  first_name: string | null;
  last_name: string | null;
  billing_email: string | null;
  company_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string;
  vat_number: string | null;
  vat_validated: boolean;
  paymentMethods: PaymentMethod[];
  workspaces: { id: number; name: string; domain: string }[];
}

export interface PaymentMethod {
  id: number;
  type: string;
  last4: string | null;
  brand: string | null;
  exp_month: number | null;
  exp_year: number | null;
  is_default: boolean;
  status: string;
}

export interface Invoice {
  id: number;
  chargebee_invoice_id: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
  billingProfile: { id: number; name: string } | null;
  workspace: { id: number; name: string } | null;
}

export interface PaymentIntentResponse {
  gateway: 'stripe' | 'authnet';
  gatewayId: string;
  intentId?: string;
  clientSecret?: string;
  stripePublishableKey?: string;
apiLoginId?: string;
  clientKey?: string;
  environment?: 'sandbox' | 'production';
}

export interface BillingAccountFormData {
  name: string;
  first_name: string;
  last_name: string;
  billing_email: string;
  company_name: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  vat_number: string;
}

export interface CardBillingInfo {
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

// ==================== NEW BILLING EVENTS TYPES ====================

export interface BillingEvent {
  id: number;
  type: 'invoice' | 'credit_note' | 'payment' | 'refund';
  chargebee_id: string;
  billing_profile_id: number;
  workspace_id: number | null;
  amount_cents: number;
  currency: string;
  status: string;
  event_date: string;
  description: string | null;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  billingProfile?: {
    id: number;
    name: string;
    company_name: string | null;
  };
  workspace?: {
    id: number;
    name: string;
    domain: string;
  } | null;
}

export interface BillingSummary {
  total_paid: number;
  total_credits: number;
  credits_available: number;
  total_refunds: number;
  outstanding: number;
}

export interface BillingHistoryResponse {
  events: BillingEvent[];
  summary: BillingSummary;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// ==================== COUNTRY CONFIGURATIONS ====================
// Full ISO 3166-1 list + behavioral metadata live in src/lib/countries.ts.

export const ALL_COUNTRIES: Country[] = COUNTRIES;
export const SCA_COUNTRIES: string[] = STRIPE_SET;        // EEA + UK + CH (Stripe-routed)
export const US_STATES: Subdivision[] = SUBDIVISIONS.US || [];

// Region no longer restricts the list — every ISO country is selectable.
export const getAvailableCountries = (_serverRegion?: string): Country[] => COUNTRIES;

export const getDefaultCountry = (_serverRegion?: string): string => '';

// Preferred helpers going forward:
export const getSubdivisions = _getSubdivisions;          // Subdivision[] | null  (US/CA/AU)
export const postalRule = _postalRule;                    // 'required' | 'optional' | 'none'
export const showTaxIdField = _showTaxIdField;            // boolean — show VAT/Tax-ID field?
export const gatewayFor = _gatewayFor;                    // 'stripe' | 'authnet'

export const getStateLabel = (country: string): string => {
  if (country === 'US') return 'State';
  if (country === 'CA') return 'Province';
  return 'State/Province';
};

// VAT/Tax-ID field label varies by regime (kept simple/generic for now).
export const getTaxIdLabel = (_country: string): string => 'VAT / Tax ID';

export const emptyBillingAccountForm = (serverRegion: string): BillingAccountFormData => ({
  name: '',
  first_name: '',
  last_name: '',
  billing_email: '',
  company_name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: getDefaultCountry(serverRegion),
  vat_number: '',
});

export const emptyCardBillingInfo = (serverRegion: string): CardBillingInfo => ({
  firstName: '',
  lastName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: getDefaultCountry(serverRegion),
});

// ==================== UTILITY FUNCTIONS ====================

export const formatCurrency = (amountCents: number, currency: string = 'USD') => {
  const amount = amountCents / 100;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Legacy formatCurrency for components still passing dollars (not cents)
export const formatCurrencyDollars = (amount: number, currency: string = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(amount);
};

export const getStatusBadge = (status: string): { bg: string; text: string; label: string } => {
  const statusColors: Record<string, { bg: string; text: string; label: string }> = {
    paid: { bg: '#dcfce7', text: '#059669', label: 'Paid' },
    success: { bg: '#dcfce7', text: '#059669', label: 'Success' },
    pending: { bg: '#fef3c7', text: '#d97706', label: 'Pending' },
    payment_due: { bg: '#fef3c7', text: '#d97706', label: 'Payment Due' },
    not_paid: { bg: '#fee2e2', text: '#dc2626', label: 'Not Paid' },
    failed: { bg: '#fee2e2', text: '#dc2626', label: 'Failed' },
    voided: { bg: '#f3f4f6', text: '#6b7280', label: 'Voided' },
    refunded: { bg: '#f3f4f6', text: '#6b7280', label: 'Credited' },
    refund_due: { bg: '#fef3c7', text: '#d97706', label: 'Credit Pending' },
    adjusted: { bg: '#f3f4f6', text: '#6b7280', label: 'Adjusted' },
    posted: { bg: '#e0e7ff', text: '#4f46e5', label: 'Posted' },
    valid: { bg: '#dcfce7', text: '#059669', label: 'Valid' },
    expiring: { bg: '#fef3c7', text: '#d97706', label: 'Expiring' },
    expired: { bg: '#fee2e2', text: '#dc2626', label: 'Expired' },
  };
  const found = statusColors[status];
  return found || { bg: '#e0e7ff', text: '#4f46e5', label: status };
};

export const getTypeBadge = (type: string): { bg: string; text: string; icon: string; label: string } => {
  const typeMap: Record<string, { bg: string; text: string; icon: string; label: string }> = {
    invoice: { bg: '#e0e7ff', text: '#4f46e5', icon: 'FileText', label: 'Invoice' },
    credit_note: { bg: '#dbeafe', text: '#1e40af', icon: 'CreditCard', label: 'Credit' },
    payment: { bg: '#dcfce7', text: '#059669', icon: 'CheckCircle', label: 'Payment' },
    refund: { bg: '#dbeafe', text: '#1e40af', icon: 'RotateCcw', label: 'Credit' },
  };
  return typeMap[type] || { bg: '#f3f4f6', text: '#6b7280', icon: 'Circle', label: type };
};
